// =============================================================================
// src/chrome/topbar/topbar-analytics-loading.ts
//
// Loading intermediario do botao Analytics. Mantem a topbar fina e reaproveita
// a tela analitica existente apos uma transicao curta com o logo do cliente.
// =============================================================================

export const ANALYTICS_LOADING_DURATION_MS = 1250;

const ANALYTICS_LOADING_SCREEN_ID = 'analyticsLoadingScreen';
const ANALYTICS_LOADING_LOGO_SRC = '/assets/analytics-loader-logo.svg';
const ANALYTICS_MIN_START_SCALE = 0.045;
let analyticsLoadingTimerId = 0;

interface AnalyticsLoadingRequest {
  readonly onComplete: () => void;
  readonly durationMs?: number;
  readonly sourceElement?: HTMLElement;
}

const ANALYTICS_LOADING_MARKUP = `<div id="${ANALYTICS_LOADING_SCREEN_ID}" class="analytics-loading-screen" role="status" aria-live="polite" aria-label="Carregando Analytics" hidden>
  <div class="analytics-loading-grid" aria-hidden="true"></div>
  <div class="analytics-loading-card">
    <span class="analytics-loading-logo-shell" aria-hidden="true">
      <span class="analytics-loading-ring analytics-loading-ring-one"></span>
      <span class="analytics-loading-ring analytics-loading-ring-two"></span>
      <img class="analytics-loading-logo" src="${ANALYTICS_LOADING_LOGO_SRC}" alt="" />
    </span>
    <p class="analytics-loading-kicker">Mapa Politico Analytics</p>
    <h2 class="analytics-loading-title">Preparando leitura estrategica</h2>
    <div class="analytics-loading-progress" aria-hidden="true"><span></span></div>
  </div>
</div>`;

/**
 * Monta o HTML da tela de loading de Analytics.
 * @example document.body.insertAdjacentHTML('beforeend', buildAnalyticsLoadingMarkup())
 */
export function buildAnalyticsLoadingMarkup(): string {
  return ANALYTICS_LOADING_MARKUP;
}

/**
 * Exibe a tela de loading e chama onComplete ao final da animacao.
 * @example showAnalyticsLoadingScreen({ onComplete: () => launcher.click() })
 */
export function showAnalyticsLoadingScreen(loadingRequest: AnalyticsLoadingRequest): void {
  window.clearTimeout(analyticsLoadingTimerId);
  const loadingScreenEl = ensureAnalyticsLoadingScreen();
  positionAnalyticsLoadingScreen(loadingScreenEl, loadingRequest.sourceElement);
  openAnalyticsLoadingScreen(loadingScreenEl);
  analyticsLoadingTimerId = window.setTimeout(() => {
    finishAnalyticsLoading(loadingScreenEl, loadingRequest.onComplete);
  }, loadingRequest.durationMs ?? ANALYTICS_LOADING_DURATION_MS);
}

function ensureAnalyticsLoadingScreen(): HTMLElement {
  const existingLoadingScreenEl = document.getElementById(ANALYTICS_LOADING_SCREEN_ID);
  if (existingLoadingScreenEl) return existingLoadingScreenEl;
  document.body.insertAdjacentHTML('beforeend', buildAnalyticsLoadingMarkup());
  return document.getElementById(ANALYTICS_LOADING_SCREEN_ID) as HTMLElement;
}

function openAnalyticsLoadingScreen(loadingScreenEl: HTMLElement): void {
  loadingScreenEl.hidden = false;
  loadingScreenEl.classList.remove('is-visible');
  void loadingScreenEl.offsetWidth;
  loadingScreenEl.classList.add('is-visible');
  document.body.classList.add('analytics-loading-open');
}

function positionAnalyticsLoadingScreen(
  loadingScreenEl: HTMLElement,
  sourceElement: HTMLElement | undefined,
): void {
  if (!sourceElement) {
    setDefaultLoadingOriginStyles(loadingScreenEl);
    return;
  }
  setLoadingOriginStyles(loadingScreenEl, sourceElement.getBoundingClientRect());
}

function setDefaultLoadingOriginStyles(loadingScreenEl: HTMLElement): void {
  loadingScreenEl.style.setProperty('--analytics-origin-x', '50vw');
  loadingScreenEl.style.setProperty('--analytics-origin-y', '32px');
  loadingScreenEl.style.setProperty('--analytics-start-scale-x', '0.12');
  loadingScreenEl.style.setProperty('--analytics-start-scale-y', '0.06');
}

function setLoadingOriginStyles(loadingScreenEl: HTMLElement, sourceElementRect: DOMRect): void {
  const centerX = sourceElementRect.left + sourceElementRect.width / 2;
  const centerY = sourceElementRect.top + sourceElementRect.height / 2;
  const viewportWidth = Math.max(window.innerWidth, 1);
  const viewportHeight = Math.max(window.innerHeight, 1);
  loadingScreenEl.style.setProperty('--analytics-origin-x', `${centerX}px`);
  loadingScreenEl.style.setProperty('--analytics-origin-y', `${centerY}px`);
  loadingScreenEl.style.setProperty('--analytics-start-scale-x', `${Math.max(sourceElementRect.width / viewportWidth, ANALYTICS_MIN_START_SCALE)}`);
  loadingScreenEl.style.setProperty('--analytics-start-scale-y', `${Math.max(sourceElementRect.height / viewportHeight, ANALYTICS_MIN_START_SCALE)}`);
}

function finishAnalyticsLoading(loadingScreenEl: HTMLElement, onComplete: () => void): void {
  loadingScreenEl.classList.remove('is-visible');
  loadingScreenEl.hidden = true;
  document.body.classList.remove('analytics-loading-open');
  analyticsLoadingTimerId = 0;
  onComplete();
}
